<template>
  <div class="active-filters-summary">
    <div class="summary-header">
      <v-icon small color="#023449">mdi-tag-multiple</v-icon>
      <span class="summary-title">Filtros Ativos:</span>
    </div>
    <div class="summary-tags">
      <!-- Período -->
      <v-chip
          v-if="hasDateRange"
          small close
          color="#023449"
          text-color="white"
          @click:close="$emit('clear', 'dateRange')"
          class="filter-chip"
          :disabled="loading"
      >
        <v-icon left x-small>mdi-calendar</v-icon>
        {{ formatDateRange() }}
      </v-chip>

      <!-- Tipo de Lançamento - Tipos -->
      <v-chip
          v-if="hasTipoLancamento"
          small close
          color="#023449"
          text-color="white"
          @click:close="$emit('clear', 'tipoLancamento')"
          class="filter-chip"
          :disabled="loading"
      >
        <v-icon left x-small>mdi-file-document-outline</v-icon>
        {{ formatTipoLancamento() }}
      </v-chip>

      <!-- Tipo de Lançamento - Status (chip separado) -->
      <v-chip
          v-if="hasStatusLancamento"
          small
          :color="getStatusColor()"
          text-color="white"
          @click:close="clearOnlyStatus"
          class="filter-chip"
          :disabled="loading"
      >
        <v-icon left x-small>mdi-progress-check</v-icon>
        Status: {{ getStatusLabel() }}
      </v-chip>

      <!-- Filiais -->
      <v-chip
          v-if="filters.filiais && filters.filiais.length > 0"
          small close
          color="#023449"
          text-color="white"
          @click:close="$emit('clear', 'filiais')"
          class="filter-chip"
          :disabled="loading"
      >
        <v-icon left x-small>mdi-office-building</v-icon>
        {{ filters.filiais.length }} filia{{ filters.filiais.length !== 1 ? 'is selecionadas' : 'l selecionada' }}
      </v-chip>

      <!-- Categorias -->
      <v-chip
          v-if="filters.categorias && filters.categorias.length > 0"
          small close
          color="#023449"
          text-color="white"
          @click:close="$emit('clear', 'categorias')"
          class="filter-chip"
          :disabled="loading"
      >
        <v-icon left x-small>mdi-shape</v-icon>
        {{ filters.categorias.length }} categoria(s)
      </v-chip>

      <!-- Marcas -->
      <v-chip
          v-if="filters.marcas && filters.marcas.length > 0"
          small close
          color="#023449"
          text-color="white"
          @click:close="$emit('clear', 'marcas')"
          class="filter-chip"
          :disabled="loading"
      >
        <v-icon left x-small>mdi-tag</v-icon>
        {{ filters.marcas.length }} marca(s)
      </v-chip>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ActiveFilters',

  props: {
    filters: { type: Object, required: true },
    tiposLancamento: { type: Array, default: () => [] },
    loading: Boolean
  },

  computed: {
    hasDateRange() {
      return this.filters.dateRange && (this.filters.dateRange.start || this.filters.dateRange.end);
    },

    hasTipoLancamento() {
      return this.filters.tipoLancamento &&
          this.filters.tipoLancamento.tipos &&
          this.filters.tipoLancamento.tipos.length > 0;
    },

    hasStatusLancamento() {
      return this.filters.tipoLancamento &&
          this.filters.tipoLancamento.status !== null &&
          this.filters.tipoLancamento.status !== undefined;
    }
  },

  methods: {
    formatDateRange() {
      const { start, end } = this.filters.dateRange;
      if (start && end) {
        return `${this.formatDate(start)} - ${this.formatDate(end)}`;
      } else if (start) {
        return `A partir de ${this.formatDate(start)}`;
      } else if (end) {
        return `Até ${this.formatDate(end)}`;
      }
      return '';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString + 'T00:00:00');
      if (isNaN(date)) return '';
      return date.toLocaleDateString('pt-BR');
    },

    formatTipoLancamento() {
      if (!this.hasTipoLancamento) return '';

      const tipos = this.filters.tipoLancamento.tipos
          .map(codigo => {
            const tipo = this.tiposLancamento.find(t => t.codigo === codigo);
            return tipo ? tipo.tipo : '';
          })
          .filter(Boolean);
      return tipos.join(', ');
    },

    getStatusLabel() {
      if (!this.hasStatusLancamento) return '';

      const statusCodigo = this.filters.tipoLancamento.status;

      // Mapeia os códigos para os labels correspondentes
      const statusMap = {
        1: 'Todos',
        2: 'Pendentes',
        3: 'Finalizados'
      };

      return statusMap[statusCodigo] || 'Desconhecido';
    },

    getStatusColor() {
      if (!this.hasStatusLancamento) return '#023449';

      const statusCodigo = this.filters.tipoLancamento.status;

      // Mapeia os códigos para as cores correspondentes
      const colorMap = {
        1: '#023449',  // "Todos"
        2: '#f59e0b',  // "Pendente"
        3: '#10b981'   // "Finalizado"
      };

      return colorMap[statusCodigo] || '#023449';
    },

    clearOnlyStatus() {
      // Emite evento especial para limpar apenas o status
      this.$emit('clear-status');
    }
  }
};
</script>

<style scoped>
.active-filters-summary {
  background: linear-gradient(135deg, rgba(2, 52, 73, 0.05) 0%, rgba(2, 52, 73, 0.02) 100%);
  border: 2px solid rgba(2, 52, 73, 0.15);
  border-radius: 12px;
  padding: 20px;
  margin: 24px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.summary-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #023449;
}

.summary-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-chip {
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(2, 52, 73, 0.1);
  transition: all 0.2s ease;
}

.filter-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(2, 52, 73, 0.15);
}

.filter-chip >>> .v-chip__close {
  transition: transform 0.2s ease;
}

.filter-chip:hover >>> .v-chip__close {
  transform: scale(1.2);
}

.filter-chip.v-chip--disabled {
  opacity: 0.6;
  pointer-events: none;
}
</style>